import React, { useEffect, useMemo, useState } from "react"
import BarChartIcon from "@mui/icons-material/BarChart"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import IconButton from "@mui/material/IconButton"
import Button from "@mui/material/Button"
import Tooltip from "@mui/material/Tooltip"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import {
  getAnalyticData,
  getAnalyticSubscriptionByDay,
  getAvgCompletedInnercise,
  getCompletedInnercise,
  getMostPopularCategoryByViews,
  getMostPopularCategoryBySpent,
  getMostPopularInnerciseByViews,
  getMostPopularInnerciseBySpent, 
  getRemindTimeStats,
  getInnerciseTimeStats,
  getPaywallData,
  getWorldClassInsights,
} from "services/stat"
import { getUnixEpochTime, getLocalTimeZone } from "utils/time"
import { SESSION_VARIABLES } from "constants/system"
import LoadingScreen from "components/common/LoadingScreen"
import { verifyResponse } from "utils/httpRequest"
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import {
  completedInnerciseToBarChar,
  RemindTimeToBarChart,
  InnerciseTimeStatsToBarChart,
} from "utils/chart"
import TextField from "@mui/material/TextField"
import moment from "moment"
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
} from "recharts"
import DataTable from "components/common/DataTable/DataTable"
import { formatTime } from "utils/time"
import SubscriptionSummary from "./templates/SubscriptionSummary"
import {
  subscribeTableCols,
  subscribeMostPopularCategoryTableCols,
  subscribeMostPopularCategoryBySpentTableCols,
  subscribeMostPopularInnerciseTableCols,
  subscribeMostPopularInnerciseSpentTableCols,
  paywallInfoTableCols,
} from "./tableData"
import BarChartDlg from "./BarChartDlg"
import {
  StyledModernDashboard,
  StyledFilterCard,
  StyledKpiGrid,
  StyledKpiCard,
  StyledInsightGrid,
  StyledInsightPanel,
  StyledInsightList,
  StyledMessageList,
  StyledAgentTrendChart,
  StyledHeatmap,
  StyledDashboardHeading,
  StyledDashboard,
  StyledInfoCard,
  StyledInfoSearchBar,
  StyledLessonEngagementChart,
  StyledEngagementKpiStrip,
  StyledEngagementSearch,
} from "./Home.style"
import { useCmsColorMode } from "context/CmsColorModeContext"
import EventsInsights from "./EventsInsights"
import EmailsInsights from "./EmailsInsights"
import NpsInsights from "./NpsInsights"
import {
  formatCompactNumber,
  formatCoveragePercent,
  formatLargeNumber,
  formatUsd,
  toFiniteNumber,
} from "./aiUsageFormatters"

const DATE_PRESETS = {
  today: { label: "Today", days: 0 },
  "7d": { label: "Last 7 Days", days: 7 },
  "30d": { label: "Last 30 Days", days: 30 },
  custom: { label: "Custom", days: null },
}

const KPI_SPARKLINE_COLORS = {
  activeUsers: "#38bdf8",
  silverTier: "#c4b5fd",
  goldTier: "#fbbf24",
  platinumTier: "#a5b4fc",
  chatPositivity: "#34d399",
  completionPerActiveUser: "#f472b6",
}

const AI_USAGE_TIERS = [
  { key: "free", label: "Free" },
  { key: "silver", label: "Silver" },
  { key: "gold", label: "Gold" },
  { key: "platinum", label: "Platinum" },
]

const getActualAiCostUsd = (usage) =>
  usage?.actualCostUsd ?? usage?.totalCostUsd ?? usage?.costUsd

const isElevenLabsWithinAllowance = (status) =>
  status === "within_allowance" || status === "covered"

const getElevenLabsIncrementalCostUsd = (elevenLabs) => {
  if (elevenLabs?.overageStatus === "rate_required") {
    return null
  }
  const cost = elevenLabs?.incrementalCostUsd ?? elevenLabs?.overageCostUsd
  return cost == null && isElevenLabsWithinAllowance(elevenLabs?.overageStatus)
    ? 0
    : cost
}

const sparklineTooltipFormatter = (value) => [value, "Value"]

const Sparkline = ({ list, color }) => {
  if (!list || list.length === 0) {
    return null
  }
  return (
    <div className="sparkline">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={list}>
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <RechartsTooltip
            formatter={sparklineTooltipFormatter}
            labelFormatter={(label) => `Day: ${label}`}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

const Home = ({ insightsView = "overview" }) => {
  const { mode: themeMode } = useCmsColorMode()
  const isOverviewInsights = insightsView === "overview"
  const isUsersInsights = insightsView === "users"
  const isAiInsights = insightsView === "ai"
  const isLessonsInsights = insightsView === "lessons"
  const isEventsInsights = insightsView === "events"
  const isEmailsInsights = insightsView === "emails"
  const isNpsInsights = insightsView === "nps"
  const freeTrialLength = 7
  const [loading, setLoading] = useState(false)
  const [analyticData, setAnalyticData] = useState(null)
  const [analyticRawData, setAnalyticRawData] = useState(null)
  const [subscribeInfo, setSubscribeInfo] = useState({
    monthlySubscription: 0,
    yearlySubscription: 0,
    liftTimeSubscription: 0,
    monthlyFreeTrial: 0,
    yearlyFreeTrial: 0,
    monthlyTotal: 0,
    yearlyTotal: 0,
    total: 0,
  })
  const [completedInnercise, setCompletedInnercise] = useState(null)
  const [avgCompletedInnercise, setAvgCompletedInnercise] = useState(null)
  const [mostPopularCategory, setMostPopularCategory] = useState(null)
  const [mostPopularCategoryBySpent, setMostPopularCategoryBySpent] =
    useState(null)
  const [mostPopularInnercise, setMostPopularInnercise] = useState(null)
  const [mostPopularInnerciseBySpent, setMostPopularInnerciseBySpent] =
    useState(null)
  const [remindTimeStats, setRemindTimeStats] = useState(null)
  const [innerciseTimeStats, setInnerciseTimeStats] = useState(null)
  const [subscribeData, setSubscribeData] = useState([])
  const [paywallData, setPaywallData] = useState([])
  const [worldClassInsights, setWorldClassInsights] = useState(null)
  const [insightsLoadError, setInsightsLoadError] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState("7d")
  const [lessonEngagementSearch, setLessonEngagementSearch] = useState("")

  const [dateRange, setDateRange] = useState([
    moment().add("days", -freeTrialLength),
    moment(),
  ])

  const [chartContent, openChartDlg] = BarChartDlg()
  const [openDataTableDlg, dataTableDlg] = DataTable()

  const getPresetRange = (presetKey) => {
    if (presetKey === "today") {
      const now = moment()
      return [now.clone().startOf("day"), now]
    }
    if (presetKey === "30d") {
      return [moment().add("days", -30), moment()]
    }
    if (presetKey === "7d") {
      return [moment().add("days", -7), moment()]
    }
    return dateRange
  }

  const getTrendClass = (deltaValue) => {
    if (deltaValue > 0) {
      return "trend-up"
    }
    if (deltaValue < 0) {
      return "trend-down"
    }
    return ""
  }

  const loadAnalyticData = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)
    if (idToken) {
      try {
        setLoading(true)
        const response = await getAnalyticData({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setAnalyticData(response.data)
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadAnalyticData, 500)
    }
  }

  const loadAnalyticSubscribeData = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)
    if (idToken) {
      try {
        setLoading(true)
        const response = await getAnalyticSubscriptionByDay({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
          localTimeZone: getLocalTimeZone(),
          freeTrialLength,
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setAnalyticRawData(response.data)
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadAnalyticData, 500)
    }
  }

  const loadAvgCompletedInnercise = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)

    if (idToken) {
      try {
        setLoading(true)
        const response = await getAvgCompletedInnercise({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
          intervalValue: 3,
          limitSize: 50,
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setAvgCompletedInnercise(response.data)
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadAvgCompletedInnercise, 500)
    }
  }

  const loadCompletedInnercise = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)

    if (idToken) {
      try {
        setLoading(true)
        const response = await getCompletedInnercise({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
          intervalValue: 5,
          limitSize: 50,
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setCompletedInnercise(response.data)
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadCompletedInnercise, 500)
    }
  }

  const loadMostPopularCategory = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)

    if (idToken) {
      try {
        setLoading(true)
        const response = await getMostPopularCategoryByViews({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
          limitSize: 50,
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setMostPopularCategory(response.data)
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadMostPopularCategory, 500)
    }
  }

  const loadMostPopularCategoryBySpent = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)

    if (idToken) {
      try {
        setLoading(true)
        const response = await getMostPopularCategoryBySpent({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
          limitSize: 50,
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setMostPopularCategoryBySpent(
            response.data.map((item) => {
              return {
                ...item,
                innercisingTime: formatTime(parseInt(item.innercisingTime)),
              }
            })
          )
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadMostPopularCategory, 500)
    }
  }

  const loadMostPopularInnercise = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)

    if (idToken) {
      try {
        setLoading(true)
        const response = await getMostPopularInnerciseByViews({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
          limitSize: 50,
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setMostPopularInnercise(
            response.data.map((item) => {
              return {
                id: item.id,
                title: item.title,
                category: item.categoryList.map((cat) => cat.name).join(","),
                views: item.views,
              }
            })
          )
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadMostPopularCategory, 500)
    }
  }

  const loadMostPopularInnerciseBySpent = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)

    if (idToken) {
      try {
        setLoading(true)
        const response = await getMostPopularInnerciseBySpent({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
          limitSize: 50,
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setMostPopularInnerciseBySpent(
            response.data.map((item) => {
              return {
                ...item,
                innercisingTime: formatTime(parseInt(item.innercisingTime)),
              }
            })
          )
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadMostPopularCategory, 500)
    }
  }

  const loadRemindTimeStats = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)

    if (idToken) {
      try {
        setLoading(true)
        const response = await getRemindTimeStats({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
          intervalValue: 5,
          limitSize: 300,
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setRemindTimeStats(response.data)
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadRemindTimeStats, 500)
    }
  }

  const loadInnerciseTimeStats = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)

    if (idToken) {
      try {
        setLoading(true)
        const response = await getInnerciseTimeStats({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
          intervalValue: 5,
          limitSize: 300,
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setInnerciseTimeStats(response.data)
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadInnerciseTimeStats, 500)
    }
  }

  const loadPaywallData = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)

    if (idToken) {
      try {
        setLoading(true)
        const response = await getPaywallData({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
          localTimeZone: getLocalTimeZone(),
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setPaywallData(
            response.data.map((paywall) => {
              return { ...paywall, id: paywall.day }
            })
          )
        }
      } catch (e) {
        console.error("loading error: ", e)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadInnerciseTimeStats, 500)
    }
  }

  const loadWorldClassInsights = async () => {
    const idToken = localStorage.getItem(SESSION_VARIABLES.ACCESS_TOKEN)

    if (idToken) {
      try {
        setLoading(true)
        const response = await getWorldClassInsights({
          startAt: getUnixEpochTime(dateRange[0]),
          endAt: getUnixEpochTime(dateRange[1]),
        })
        verifyResponse(response)
        if (response.rtnCode === 200) {
          setWorldClassInsights(response.data)
          setInsightsLoadError(false)
        } else {
          setInsightsLoadError(true)
        }
      } catch (e) {
        console.error("loading error: ", e)
        setInsightsLoadError(true)
      } finally {
        setLoading(false)
      }
    } else {
      setTimeout(loadWorldClassInsights, 500)
    }
  }

  useEffect(() => {
    console.log("[CMS Dashboard]", { insightsView })
  }, [insightsView])

  useEffect(() => {
    if (!isOverviewInsights) {
      return
    }
    loadAnalyticData()
    loadAnalyticSubscribeData()
  }, [isOverviewInsights])

  useEffect(() => {
    let monthly = 0
    let yearly = 0
    let lifeTime = 0
    let total = 0
    let monthlyFreeTrial = 0
    let yearlyFreeTrial = 0
    let totalMonthlyConvertCount = 0
    let totalYearlyConvertCount = 0
    let totalPreviousMonthlyTrialCount = 0
    let totalPreviousYearlyTrialCount = 0

    if (analyticRawData) {
      for (const record of analyticRawData) {
        monthly += parseInt(record.monthlySubStartCount)
        monthlyFreeTrial += parseInt(record.monthlyTrialCount)
        yearly += parseInt(record.yearlySubStartCount)
        yearlyFreeTrial += parseInt(record.yearlyTrialCount)
        lifeTime += parseInt(record.lifeTimeCount)
        total += parseInt(record.total)
        totalMonthlyConvertCount += record.monthlyConvertCount
          ? parseInt(record.monthlyConvertCount)
          : 0
        totalYearlyConvertCount += record.yearlyConvertCount
          ? parseInt(record.yearlyConvertCount)
          : 0
        totalPreviousMonthlyTrialCount += record.previousMonthlyTrialCount
          ? parseInt(record.previousMonthlyTrialCount)
          : 0

        totalPreviousYearlyTrialCount += record.previousYearlyTrialCount
          ? parseInt(record.previousYearlyTrialCount)
          : 0
      }

      setSubscribeInfo({
        monthlySubscription: monthly,
        yearlySubscription: yearly,
        liftTimeSubscription: lifeTime,
        monthlyFreeTrial,
        yearlyFreeTrial,
        monthlyTotal: monthly + monthlyFreeTrial,
        yearlyTotal: yearly + yearlyFreeTrial,
        totalMonthlyConvertRate:
          totalPreviousMonthlyTrialCount === 0
            ? 0
            : `${parseInt(
                (totalMonthlyConvertCount / totalPreviousMonthlyTrialCount) *
                  100
              )}%`,
        totalYearlyConvertRate:
          totalPreviousYearlyTrialCount === 0
            ? 0
            : `${parseInt(
                (totalYearlyConvertCount / totalPreviousYearlyTrialCount) * 100
              )}%`,
        total,
      })

      // console.log("subscribeInfo: ", subscribeInfo)

      const days = dateRange[1].diff(dateRange[0], "days")

      const dateList = []
      for (let i = 0; i < days; i++) {
        const date = dateRange[0].clone().add("days", i)
        dateList.push(date.format("YYYY-MM-DD"))
      }

      const analyticData = []

      for (let dateString of dateList) {
        const dateRelatedObj = analyticRawData.find(
          (item) => item.day === dateString
        )

        const dataObj = {
          id: dateString,
          day: dateString,
          totalSubscription: dateRelatedObj
            ? parseInt(dateRelatedObj.total)
            : 0,
          monthlyFreeTrialCount: dateRelatedObj
            ? parseInt(dateRelatedObj.monthlyTrialCount)
            : 0,
          monthlyCount: dateRelatedObj
            ? parseInt(dateRelatedObj.monthlySubStartCount)
            : 0,
          yearlyFreeTrialCount: dateRelatedObj
            ? parseInt(dateRelatedObj.yearlyTrialCount)
            : 0,
          yearlyCount: dateRelatedObj
            ? parseInt(dateRelatedObj.yearlySubStartCount)
            : 0,
          lifeTimeCount: dateRelatedObj
            ? parseInt(dateRelatedObj.lifeTimeCount)
            : 0,
          monthlyConvertRate: dateRelatedObj
            ? parseInt(dateRelatedObj.monthlyConvertRate) * 100
            : 0,
          yearlyConvertRate: dateRelatedObj
            ? parseInt(dateRelatedObj.yearlyConvertRate) * 100
            : 0,
        }
        analyticData.push(dataObj)
      }

      setSubscribeData(analyticData)
    }
  }, [analyticRawData])

  useEffect(() => {
    if (isEventsInsights) {
      // EventsInsights loads its own data from the eventInsights endpoint.
      return
    }
    if (isEmailsInsights) {
      // EmailsInsights loads its own paginated data.
      return
    }
    if (isNpsInsights) {
      // NpsInsights loads its own paginated data.
      return
    }
    if (isLessonsInsights) {
      loadWorldClassInsights()
      return
    }
    if (isUsersInsights) {
      loadWorldClassInsights()
      return
    }
    if (isAiInsights) {
      loadWorldClassInsights()
      return
    }
    loadAnalyticData()
    loadAvgCompletedInnercise()
    loadCompletedInnercise()
    loadMostPopularCategory()
    loadMostPopularCategoryBySpent()
    loadMostPopularInnercise()
    loadMostPopularInnerciseBySpent()
    loadRemindTimeStats()
    loadInnerciseTimeStats()
    loadAnalyticSubscribeData()
    loadPaywallData()
    loadWorldClassInsights()
  }, [dateRange, isLessonsInsights, isUsersInsights, isAiInsights, isEventsInsights, isEmailsInsights, isNpsInsights])

  const handlePresetChange = (_, newPreset) => {
    if (!newPreset) {
      return
    }
    setSelectedPreset(newPreset)
    if (newPreset !== "custom") {
      setDateRange(getPresetRange(newPreset))
    }
  }

  const formatPct = (value) => {
    const parsed = Number(value || 0)
    if (Number.isNaN(parsed)) {
      return "0.00%"
    }
    return `${parsed.toFixed(2)}%`
  }

  const topAgents = worldClassInsights?.chat?.topAgents || []
  const topRatedMessages = worldClassInsights?.chat?.topRatedMessages || []
  const downvotedMessages = worldClassInsights?.chat?.downvotedMessages || []
  const popularEvents = worldClassInsights?.events?.mostPopular || []
  const programsCompleted = worldClassInsights?.learning?.programsCompleted || []
  const lessonsCompleted = worldClassInsights?.learning?.lessonsCompleted || []
  const lessonsMostRewatched =
    worldClassInsights?.learning?.lessonsMostRewatched || []

  const activeUsersInsight = worldClassInsights?.kpis?.activeUsers
  const tierMembers = worldClassInsights?.kpis?.tierMembers
  const userBreakdown = worldClassInsights?.kpis?.userBreakdown
  const userBreakdownByTier = userBreakdown?.byTier || []
  const userBreakdownTotals = userBreakdown?.totals || {}
  const activeNowUsers = userBreakdown?.activeNowUsers || []
  const activeNowWindowMinutes = userBreakdown?.activeNowWindowMinutes || 15
  const tierGrantsCurrent = worldClassInsights?.kpis?.tierGrants?.current
  const tierGrantsPrevious = worldClassInsights?.kpis?.tierGrants?.previous
  const chatSummary = worldClassInsights?.chat?.summary
  const usageSummary = worldClassInsights?.chat?.usageSummary
  const elevenLabsSummary = usageSummary?.elevenLabs
  const usageByTier = worldClassInsights?.chat?.usageByTier
  const usageDaily = worldClassInsights?.chat?.usageDaily
  const extraInsights = worldClassInsights?.extraInsights
  const sparklines = worldClassInsights?.sparklines || {}
  const agentTrend = worldClassInsights?.chat?.agentTrend
  const cohortHeatmap = worldClassInsights?.learning?.lessonCompletionCohorts?.heatmap || []
  const cohortRows = worldClassInsights?.learning?.lessonCompletionCohorts?.rows || []
  const cohortWeekOffsets =
    worldClassInsights?.learning?.lessonCompletionCohorts?.weekOffsets || []
  const programLessonEngagementFunnel =
    worldClassInsights?.learning?.programLessonEngagementFunnel || []

  const aiTierUsage = useMemo(() => {
    return AI_USAGE_TIERS.map((tier) => {
      const row = (usageByTier || []).find(
        (item) => String(item?.tierKey || "").toLowerCase() === tier.key
      )
      return { ...tier, ...(row || {}) }
    })
  }, [usageByTier])

  const aiUsageTrend = useMemo(() => {
    return (usageDaily || [])
      .filter((row) => row && row.day)
      .map((row) => ({
        day: row.day,
        inputTokens: toFiniteNumber(row.inputTokens),
        outputTokens: toFiniteNumber(row.outputTokens),
        totalTokens: toFiniteNumber(row.totalTokens),
        totalCostUsd: toFiniteNumber(row.totalCostUsd),
      }))
  }, [usageDaily])

  const filteredLessonEngagementFunnel = useMemo(() => {
    const q = lessonEngagementSearch.trim().toLowerCase()
    if (!q) {
      return programLessonEngagementFunnel
    }
    return programLessonEngagementFunnel.filter((row) => {
      const programTitle = String(row.programTitle || "").toLowerCase()
      const lessonTitle = String(row.lessonTitle || "").toLowerCase()
      const lessonId = String(row.lessonId ?? "")
      const programId = String(row.programId ?? "")
      return (
        programTitle.includes(q) ||
        lessonTitle.includes(q) ||
        lessonId.includes(q) ||
        programId.includes(q)
      )
    })
  }, [programLessonEngagementFunnel, lessonEngagementSearch])

  const lessonEngagementKpis = useMemo(() => {
    const rows = filteredLessonEngagementFunnel
    const programIds = new Set(rows.map((r) => r.programId))
    let completionAcc = 0
    let completionRows = 0
    let watchSum = 0
    let watchN = 0
    let dropoffAlerts = 0
    for (const r of rows) {
      const m = Number(r.membersWithActivity) || 0
      const c = Number(r.membersCompleted) || 0
      if (m > 0) {
        completionAcc += c / m
        completionRows += 1
      }
      const w = Number(r.avgMaxWatchPct)
      if (!Number.isNaN(w)) {
        watchSum += w
        watchN += 1
      }
      const d = Number(r.sequentialDropoffPct)
      if (!Number.isNaN(d) && d >= 15) {
        dropoffAlerts += 1
      }
    }

    const avgLessonCompletionRate =
      completionRows > 0 ? Number(((completionAcc / completionRows) * 100).toFixed(2)) : 0
    const avgWatchDepth = watchN > 0 ? Number((watchSum / watchN).toFixed(2)) : 0

    return {
      programsInView: programIds.size,
      lessonRows: rows.length,
      avgLessonCompletionRate,
      avgWatchDepth,
      highDropoffLessons: dropoffAlerts,
    }
  }, [filteredLessonEngagementFunnel])

  const lessonEngagementBarData = useMemo(() => {
    const sorted = [...filteredLessonEngagementFunnel].sort((a, b) => {
      if (a.programId !== b.programId) {
        return a.programId - b.programId
      }
      const oa = Number(a.lessonOrder) || 0
      const ob = Number(b.lessonOrder) || 0
      if (oa !== ob) {
        return oa - ob
      }
      return (a.lessonId || 0) - (b.lessonId || 0)
    })
    const maxBars = 28
    return sorted.slice(0, maxBars).map((r) => {
      const title = r.lessonTitle || "Lesson"
      const shortTitle = title.length > 16 ? `${title.slice(0, 16)}…` : title
      const m = Number(r.membersWithActivity) || 0
      const c = Number(r.membersCompleted) || 0
      return {
        key: `${r.programId}-${r.lessonId}`,
        label: shortTitle,
        fullLesson: title,
        program: r.programTitle || "",
        active: m,
        completed: c,
        completionRate: m > 0 ? Number(((c / m) * 100).toFixed(1)) : 0,
        dropoff:
          r.sequentialDropoffPct != null && r.sequentialDropoffPct !== ""
            ? Number(r.sequentialDropoffPct)
            : null,
        avgWatch: Number(r.avgMaxWatchPct) || 0,
      }
    })
  }, [filteredLessonEngagementFunnel])

  const insightsScope = isLessonsInsights
    ? "lessons"
    : isUsersInsights
      ? "users"
      : isAiInsights
        ? "ai"
        : "overview"

  const heatmapColor = (retentionRate) => {
    const value = Number(retentionRate || 0)
    if (value >= 80) return "#22c55e"
    if (value >= 60) return "#84cc16"
    if (value >= 40) return "#facc15"
    if (value >= 20) return "#fb923c"
    return "#f87171"
  }

  const openPanelTable = (title, rows, fields) => {
    const columns = fields.map((field) => ({
      field: field.key,
      headerName: field.label,
      minWidth: field.minWidth || 120,
      width: field.width || 180,
      flex: field.flex || 1,
    }))
    const normalizedRows = (rows || []).map((row, idx) => {
      return {
        ...row,
        id: row.id || row.eventId || row.programId || row.lessonId || `${title}-${idx}`,
      }
    })
    openDataTableDlg({
      title,
      columns,
      rows: normalizedRows,
    })
  }

  const openActiveNowTable = () => {
    openPanelTable(
      `Active now: last ${activeNowWindowMinutes} minutes`,
      activeNowUsers.map((user) => ({
        ...user,
        name: [user.firstName, user.lastName].filter(Boolean).join(" ") || "-",
        tier: user.tierLabel || user.tierType || "Free",
        lastActivityAt: user.lastActivityAt
          ? moment(user.lastActivityAt).format("YYYY-MM-DD HH:mm:ss")
          : "-",
      })),
      [
        { key: "memberId", label: "Member ID", width: 110 },
        { key: "name", label: "Name", width: 180 },
        { key: "email", label: "Email", width: 260 },
        { key: "communityHandle", label: "Handle", width: 160 },
        { key: "tier", label: "Tier", width: 120 },
        { key: "lastActivitySource", label: "Last Activity", width: 180 },
        { key: "lastActivityAt", label: "Activity At", width: 190 },
      ]
    )
  }

  return (
    <>
      <StyledModernDashboard
        className={themeMode === "dark" ? "theme-dark" : "theme-light"}
      >
        <StyledDashboardHeading>
          {isLessonsInsights
            ? "Lessons insights"
            : isUsersInsights
              ? "Users insights"
              : isAiInsights
                ? "AI insights"
                : isEventsInsights
                  ? "Events insights"
                  : isEmailsInsights
                    ? "Email insights"
                    : isNpsInsights
                      ? "NPS insights"
                  : "CMS Insights"}
        </StyledDashboardHeading>
        <StyledFilterCard className={themeMode === "dark" ? "dark" : ""}>
          <div className="filter-title">
            Date Range and Period Comparison
          </div>
          <div className="filter-row">
            <ToggleButtonGroup
              value={selectedPreset}
              exclusive
              size="small"
              onChange={handlePresetChange}
            >
              <ToggleButton value="today">
                {DATE_PRESETS.today.label}
              </ToggleButton>
              <ToggleButton value="7d">
                {DATE_PRESETS["7d"].label}
              </ToggleButton>
              <ToggleButton value="30d">
                {DATE_PRESETS["30d"].label}
              </ToggleButton>
              <ToggleButton value="custom">
                {DATE_PRESETS.custom.label}
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
          <StyledInfoSearchBar>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateRangePicker
                className="media-search-range-picker"
                value={dateRange}
                onOpen={() => {
                  setTimeout(() => {
                    ;[...document.querySelectorAll("div")]
                      .filter((a) => a.textContent === "MUI X: Missing license key")
                      .forEach((a) => a.remove())
                  }, 0)
                }}
                onChange={(newValue) => {
                  if (newValue?.[0] && newValue?.[1]) {
                    setSelectedPreset("custom")
                    setDateRange([
                      moment(newValue[0]).startOf("day"),
                      moment(newValue[1]).endOf("day"),
                    ])
                  }
                }}
                renderInput={(startProps, endProps) => (
                  <>
                    <TextField {...startProps} placeholder="Date From" className="time-input" />
                    <TextField {...endProps} placeholder="Date To" className="time-input" />
                  </>
                )}
              />
            </LocalizationProvider>
          </StyledInfoSearchBar>
          {insightsLoadError && (
            <div className="insights-warning">
              Insights API did not load. Confirm latest `neurogym-app` stage build is deployed.
            </div>
          )}
        </StyledFilterCard>

        {isEventsInsights && (
          <EventsInsights dateRange={dateRange} themeMode={themeMode} />
        )}

        {isEmailsInsights && (
          <EmailsInsights dateRange={dateRange} themeMode={themeMode} />
        )}

        {isNpsInsights && (
          <NpsInsights dateRange={dateRange} themeMode={themeMode} />
        )}

        {isOverviewInsights && (
        <StyledKpiGrid>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Active Users</div>
            <div className="value">{activeUsersInsight?.current ?? 0}</div>
            <div className={`meta ${getTrendClass(activeUsersInsight?.delta ?? 0)}`}>
              {`vs previous period: ${activeUsersInsight?.delta ?? 0} (${formatPct(
                activeUsersInsight?.deltaPct
              )})`}
            </div>
            <div className="meta">{`Source: ${
              activeUsersInsight?.source === "firstPartyActivity"
                ? "First-party activity"
                : "Google Analytics"
            }`}</div>
            <Sparkline
              list={sparklines.activeUsers}
              color={KPI_SPARKLINE_COLORS.activeUsers}
            />
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Silver Tier Members</div>
            <div className="value">{tierMembers?.silver ?? 0}</div>
            <div className="meta">
              {`New in range: ${tierGrantsCurrent?.silver ?? 0} (prev ${
                tierGrantsPrevious?.silver ?? 0
              })`}
            </div>
            <Sparkline
              list={sparklines.silverTier}
              color={KPI_SPARKLINE_COLORS.silverTier}
            />
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Gold Tier Members</div>
            <div className="value">{tierMembers?.gold ?? 0}</div>
            <div className="meta">
              {`New in range: ${tierGrantsCurrent?.gold ?? 0} (prev ${
                tierGrantsPrevious?.gold ?? 0
              })`}
            </div>
            <Sparkline
              list={sparklines.goldTier}
              color={KPI_SPARKLINE_COLORS.goldTier}
            />
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Platinum Tier Members</div>
            <div className="value">{tierMembers?.platinum ?? 0}</div>
            <div className="meta">
              {`New in range: ${tierGrantsCurrent?.platinum ?? 0} (prev ${
                tierGrantsPrevious?.platinum ?? 0
              })`}
            </div>
            <Sparkline
              list={sparklines.platinumTier}
              color={KPI_SPARKLINE_COLORS.platinumTier}
            />
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Lesson Completions per Active User</div>
            <div className="value">{extraInsights?.completionPerActiveUser ?? 0}</div>
            <div className="meta">Engagement velocity for current period</div>
            <Sparkline
              list={sparklines.completionPerActiveUser}
              color={KPI_SPARKLINE_COLORS.completionPerActiveUser}
            />
          </StyledKpiCard>
        </StyledKpiGrid>
        )}

        {isAiInsights && (
        <StyledKpiGrid>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Chat Positivity Rate</div>
            <div className="value">{formatPct(chatSummary?.positivityRate)}</div>
            <div className="meta">
              {`Votes: ${chatSummary?.upvotes ?? 0} up / ${chatSummary?.downvotes ?? 0} down`}
            </div>
            <Sparkline
              list={sparklines.chatPositivity}
              color={KPI_SPARKLINE_COLORS.chatPositivity}
            />
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">AI Turns</div>
            <div className="value">{formatCompactNumber(usageSummary?.turns)}</div>
            <div className="meta">Completed coach exchanges in this period</div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Total AI Users</div>
            <div className="value">{formatLargeNumber(usageSummary?.users)}</div>
            <div className="meta">
              Distinct members with AI activity; denominator for per-user averages
            </div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Average AI Turns per User</div>
            <div className="value">
              {formatLargeNumber(usageSummary?.averageTurnsPerUser)}
            </div>
            <div className="meta">Completed turns divided by distinct AI users</div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Individual Messages</div>
            <div className="value">
              {formatCompactNumber(usageSummary?.individualMessages)}
            </div>
            <div className="meta">User and coach messages combined</div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Total AI Tokens</div>
            <Tooltip title={formatLargeNumber(usageSummary?.totalTokens)}>
              <div className="value">{formatCompactNumber(usageSummary?.totalTokens)}</div>
            </Tooltip>
            <div className="meta">
              {`${formatCompactNumber(usageSummary?.inputTokens)} input / ${formatCompactNumber(
                usageSummary?.outputTokens
              )} output`}
            </div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Average AI Tokens per User</div>
            <Tooltip title={formatLargeNumber(usageSummary?.averageTokensPerUser)}>
              <div className="value">
                {formatCompactNumber(usageSummary?.averageTokensPerUser)}
              </div>
            </Tooltip>
            <div className="meta">Total tokens divided by distinct AI users</div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Actual AI Cost</div>
            <div className="value">{formatUsd(getActualAiCostUsd(usageSummary))}</div>
            <div className="meta">
              Metered non-ElevenLabs cost plus allocated ElevenLabs subscription and overage
            </div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Average Cost per Turn</div>
            <div className="value">
              {formatUsd(usageSummary?.averageCostPerTurnUsd)}
            </div>
            <div className="meta">Actual cost divided by completed turns</div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">Cost per AI User</div>
            <div className="value">
              {formatUsd(usageSummary?.averageCostPerUserUsd)}
            </div>
            <div className="meta">
              {`${formatCompactNumber(usageSummary?.users)} users with AI activity`}
            </div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">ElevenLabs Allocated Subscription</div>
            <div className="value">
              {formatUsd(elevenLabsSummary?.allocatedSubscriptionCostUsd)}
            </div>
            <div className="meta">
              {`${formatUsd(elevenLabsSummary?.monthlyBaseFeeUsd)} monthly base fee allocated by credit usage`}
            </div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">ElevenLabs Credits Used</div>
            <Tooltip title={formatLargeNumber(elevenLabsSummary?.creditsUsed)}>
              <div className="value">
                {`${formatCompactNumber(elevenLabsSummary?.creditsUsed)} / ${formatCompactNumber(
                  elevenLabsSummary?.includedCreditsPerMonth
                )}`}
              </div>
            </Tooltip>
            <div className="meta">
              {`${formatCoveragePercent(elevenLabsSummary?.creditUtilizationPercent)} of monthly included credits`}
            </div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">ElevenLabs Usage Valuation</div>
            <div className="value">{formatUsd(elevenLabsSummary?.usageValuationUsd)}</div>
            <div className="meta">
              Value of credits consumed—not incremental invoice spend
            </div>
          </StyledKpiCard>
          <StyledKpiCard className={themeMode === "light" ? "light" : ""}>
            <div className="label">ElevenLabs Overage / Incremental Cost</div>
            <div className="value">
              {formatUsd(getElevenLabsIncrementalCostUsd(elevenLabsSummary))}
            </div>
            <div className="meta">
              {elevenLabsSummary?.overageStatus === "rate_required"
                ? "Overage rate required to calculate cost"
                : isElevenLabsWithinAllowance(elevenLabsSummary?.overageStatus)
                  ? "Covered by included monthly credits"
                  : `${formatCompactNumber(elevenLabsSummary?.overageCredits)} overage credits`}
            </div>
          </StyledKpiCard>
        </StyledKpiGrid>
        )}

        {isAiInsights && (
        <>
          <StyledInsightPanel
            className={themeMode === "dark" ? "dark ai-usage-panel" : "ai-usage-panel"}
            style={{ width: "100%", minHeight: "auto", marginBottom: "16px" }}
          >
            <div className="panel-header">
              <div>
                <h3 className="panel-title">AI Usage by Membership Tier</h3>
                <div className="secondary">
                  Free, Silver, Gold, and Platinum usage for the selected period.
                  Actual cost includes metered non-ElevenLabs usage plus each tier&apos;s
                  allocated ElevenLabs subscription and overage.
                </div>
              </div>
              <Tooltip title="The $330 monthly ElevenLabs subscription is allocated to tiers by each tier's share of credits used. Usage valuation shows the value of consumed credits for comparison; it is not additional invoice spend. Actual cost includes the allocated subscription and any calculable overage.">
                <span className="accounting-help">How tier accounting works</span>
              </Tooltip>
            </div>
            {!usageSummary && (!usageByTier || usageByTier.length === 0) ? (
              <div className="empty">
                AI usage accounting is not available from this API response yet.
              </div>
            ) : (
              <div className="tier-breakdown-grid ai-tier-grid">
                {aiTierUsage.map((tier) => (
                  <div className="tier-breakdown-card" key={tier.key}>
                    <div className="tier-label">{tier.tierLabel || tier.label}</div>
                    <div className="tier-count-row">
                      <span>AI users</span>
                      <strong>{formatLargeNumber(tier.users)}</strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Turns</span>
                      <strong>{formatLargeNumber(tier.turns)}</strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Messages</span>
                      <strong>{formatLargeNumber(tier.individualMessages)}</strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Input tokens</span>
                      <strong>{formatCompactNumber(tier.inputTokens)}</strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Output tokens</span>
                      <strong>{formatCompactNumber(tier.outputTokens)}</strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Total tokens</span>
                      <strong title={formatLargeNumber(tier.totalTokens)}>
                        {formatCompactNumber(tier.totalTokens)}
                      </strong>
                    </div>
                    <div className="tier-count-row">
                      <span>ElevenLabs credits</span>
                      <strong>{formatCompactNumber(tier.elevenLabsCredits)}</strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Credit share</span>
                      <strong>
                        {formatCoveragePercent(tier.elevenLabsCreditSharePercent)}
                      </strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Allocated subscription</span>
                      <strong>{formatUsd(tier.allocatedElevenLabsSubscriptionUsd)}</strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Usage valuation</span>
                      <strong>{formatUsd(tier.elevenLabsUsageValuationUsd)}</strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Overage / incremental</span>
                      <strong>{formatUsd(tier.elevenLabsOverageCostUsd)}</strong>
                    </div>
                    <div className="tier-count-row cost-row">
                      <span>Actual cost</span>
                      <strong>{formatUsd(tier.totalCostUsd)}</strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Cost / user</span>
                      <strong>{formatUsd(tier.averageCostPerUserUsd)}</strong>
                    </div>
                    <div className="tier-count-row">
                      <span>Avg / turn</span>
                      <strong>{formatUsd(tier.averageCostPerTurnUsd)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </StyledInsightPanel>

          <StyledInsightPanel
            className={themeMode === "dark" ? "dark ai-usage-panel" : "ai-usage-panel"}
            style={{ width: "100%", minHeight: "auto", marginBottom: "16px" }}
          >
            <div className="panel-header">
              <div>
                <h3 className="panel-title">Accounting Coverage</h3>
                <div className="secondary">
                  Actual cost combines metered non-ElevenLabs usage with allocated ElevenLabs
                  subscription and calculable overage. ElevenLabs usage valuation is a
                  comparison metric, not additional invoice spend.
                </div>
              </div>
              <Tooltip title="Measured and estimated percentages describe usage-data coverage. Actual cost uses metered non-ElevenLabs cost plus ElevenLabs subscription allocation and known overage. ElevenLabs credit valuation is shown separately and is not added as incremental invoice spend.">
                <span className="accounting-help">What is covered?</span>
              </Tooltip>
            </div>
            {!usageSummary ? (
              <div className="empty">No accounting coverage details available.</div>
            ) : (
              <>
                <div className="coverage-summary">
                  <div className="coverage-stat">
                    <span>Measured</span>
                    <strong>{formatCoveragePercent(usageSummary.measuredPercent)}</strong>
                  </div>
                  <div className="coverage-stat">
                    <span>Estimated</span>
                    <strong>{formatCoveragePercent(usageSummary.estimatedPercent)}</strong>
                  </div>
                  <div className="coverage-stat warning">
                    <span>Unpriced events</span>
                    <strong>{formatLargeNumber(usageSummary.unpricedEventCount)}</strong>
                  </div>
                </div>
                <div className="coverage-notes">
                  Historical coach usage is approximated from saved prompts and messages and
                  attributed to each member&apos;s current effective tier. Future usage uses
                  provider measurements where available and preserves the tier at event time.
                  The ElevenLabs monthly subscription is allocated by tier credit share;
                  credit valuation is informational and not incremental invoice spend.
                </div>
              </>
            )}
          </StyledInsightPanel>

          {aiUsageTrend.length > 0 && (
            <StyledInsightGrid>
              <StyledInsightPanel className={themeMode === "dark" ? "dark" : ""}>
                <div className="panel-header">
                  <div>
                    <h3 className="panel-title">Daily Token Usage</h3>
                    <div className="secondary">Input, output, and total tokens by day.</div>
                  </div>
                </div>
                <StyledAgentTrendChart>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={aiUsageTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={formatCompactNumber} width={58} />
                      <RechartsTooltip formatter={(value, name) => [formatLargeNumber(value), name]} />
                      <Legend />
                      <Line type="monotone" dataKey="inputTokens" name="Input tokens" stroke="#38bdf8" dot={false} />
                      <Line type="monotone" dataKey="outputTokens" name="Output tokens" stroke="#a78bfa" dot={false} />
                      <Line type="monotone" dataKey="totalTokens" name="Total tokens" stroke="#34d399" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </StyledAgentTrendChart>
              </StyledInsightPanel>
              <StyledInsightPanel className={themeMode === "dark" ? "dark" : ""}>
                <div className="panel-header">
                  <div>
                    <h3 className="panel-title">Daily Actual AI Cost</h3>
                    <div className="secondary">Actual provider cost and supported estimates by day.</div>
                  </div>
                </div>
                <StyledAgentTrendChart>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={aiUsageTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={formatUsd} width={72} />
                      <RechartsTooltip formatter={(value) => [formatUsd(value), "Cost"]} />
                      <Line type="monotone" dataKey="totalCostUsd" name="Cost (USD)" stroke="#f59e0b" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </StyledAgentTrendChart>
              </StyledInsightPanel>
            </StyledInsightGrid>
          )}
        </>
        )}

        {isLessonsInsights && (
        <>
        <StyledDashboardHeading style={{ fontSize: "18px", marginBottom: "6px" }}>
          Program lesson engagement
        </StyledDashboardHeading>
        <StyledInsightPanel
          className={themeMode === "dark" ? "dark" : ""}
          style={{ width: "100%", minHeight: "auto", marginBottom: "20px" }}
        >
          <div className="panel-header">
            <div>
              <h3 className="panel-title">Watch coverage and drop-off by lesson</h3>
              <div
                className="secondary"
                style={{
                  fontSize: "12px",
                  marginTop: "6px",
                  color: themeMode === "dark" ? "#94a3b8" : "#64748b",
                }}
              >
                From tracked viewing (completion when coverage ≥ 90%). Uses the same date range as insights
                above. Search filters programs and innercises.
              </div>
            </div>
            <div className="panel-actions">
              <Tooltip title="Open full funnel table">
                <IconButton
                  size="small"
                  onClick={() =>
                    openPanelTable(
                      "Program lesson engagement",
                      filteredLessonEngagementFunnel.map((r, idx) => ({
                        ...r,
                        id: `${r.programId}-${r.lessonId}-${idx}`,
                      })),
                      [
                        { key: "programTitle", label: "Program", width: 220 },
                        { key: "lessonTitle", label: "Innercise", width: 240 },
                        { key: "lessonOrder", label: "Order", width: 80 },
                        { key: "membersWithActivity", label: "Learners (activity)", width: 140 },
                        { key: "membersCompleted", label: "Completed", width: 100 },
                        { key: "avgMaxWatchPct", label: "Avg max watch %", width: 130 },
                        { key: "sequentialDropoffPct", label: "Seq. drop-off %", width: 130 },
                      ],
                    )
                  }
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <StyledEngagementSearch>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search program, innercise, or ID…"
              value={lessonEngagementSearch}
              onChange={(e) => setLessonEngagementSearch(e.target.value)}
            />
          </StyledEngagementSearch>

          <StyledEngagementKpiStrip className={themeMode === "dark" ? "dark" : ""}>
            <div className="engagement-kpi">
              <div className="kpi-label">Programs in view</div>
              <div className="kpi-value">{lessonEngagementKpis.programsInView}</div>
              <div className="kpi-hint">Distinct programs after search</div>
            </div>
            <div className="engagement-kpi">
              <div className="kpi-label">Lesson rows</div>
              <div className="kpi-value">{lessonEngagementKpis.lessonRows}</div>
              <div className="kpi-hint">Innercises in filtered set</div>
            </div>
            <div className="engagement-kpi">
              <div className="kpi-label">Avg completion rate</div>
              <div className="kpi-value">{formatPct(lessonEngagementKpis.avgLessonCompletionRate)}</div>
              <div className="kpi-hint">Completed divided by active learners, per lesson</div>
            </div>
            <div className="engagement-kpi">
              <div className="kpi-label">Avg max watch</div>
              <div className="kpi-value">{formatPct(lessonEngagementKpis.avgWatchDepth)}</div>
              <div className="kpi-hint">Depth of viewing (max %)</div>
            </div>
            <div className="engagement-kpi">
              <div className="kpi-label">High drop-off lessons</div>
              <div className="kpi-value">{lessonEngagementKpis.highDropoffLessons}</div>
              <div className="kpi-hint">Sequential drop-off ≥ 15%</div>
            </div>
          </StyledEngagementKpiStrip>

          {programLessonEngagementFunnel.length === 0 ? (
            <div className="empty">
              No program lesson engagement in this period yet, or data is still collecting.
            </div>
          ) : filteredLessonEngagementFunnel.length === 0 ? (
            <div className="empty">No lessons match your search.</div>
          ) : (
            <>
              {filteredLessonEngagementFunnel.length > lessonEngagementBarData.length && (
                <div className="empty" style={{ marginBottom: "6px" }}>
                  {`Chart shows first ${lessonEngagementBarData.length} lessons in program order; open table for all.`}
                </div>
              )}
              <StyledLessonEngagementChart>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={lessonEngagementBarData} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="label"
                      interval={0}
                      angle={-32}
                      textAnchor="end"
                      height={70}
                      tick={{ fontSize: 10, fill: themeMode === "dark" ? "#94a3b8" : "#475569" }}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: themeMode === "dark" ? "#94a3b8" : "#475569" }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: themeMode === "dark" ? "#1e293b" : "#fff",
                        border: "1px solid #cbd5e1",
                        borderRadius: 8,
                      }}
                      formatter={(value, name) => [value, name]}
                      labelFormatter={(_, payload) => {
                        const p = payload && payload[0] && payload[0].payload
                        if (!p) return ""
                        return `${p.fullLesson} — ${p.program}`
                      }}
                    />
                    <Legend />
                    <Bar dataKey="active" name="Learners w/ activity" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="completed" name="Completed (≥90% watch)" fill="#34d399" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </StyledLessonEngagementChart>
              {lessonEngagementBarData.some(
                (d) => d.dropoff != null && Number.isFinite(d.dropoff),
              ) && (
                <>
                  <div
                    className="secondary"
                    style={{
                      fontSize: "12px",
                      marginTop: "12px",
                      marginBottom: "4px",
                      color: themeMode === "dark" ? "#94a3b8" : "#64748b",
                    }}
                  >
                    Sequential drop-off: share of learners who reached the prior lesson in order but did not show
                    activity on this lesson (null for first lesson in program).
                  </div>
                  <StyledLessonEngagementChart style={{ height: "min(240px, 40vh)" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={lessonEngagementBarData}
                        margin={{ top: 8, right: 8, left: 0, bottom: 48 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="label"
                          interval={0}
                          angle={-32}
                          textAnchor="end"
                          height={70}
                          tick={{ fontSize: 10, fill: themeMode === "dark" ? "#94a3b8" : "#475569" }}
                        />
                        <YAxis
                          domain={[0, "auto"]}
                          tick={{ fontSize: 11, fill: themeMode === "dark" ? "#94a3b8" : "#475569" }}
                          tickFormatter={(v) => `${v}%`}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: themeMode === "dark" ? "#1e293b" : "#fff",
                            border: "1px solid #cbd5e1",
                            borderRadius: 8,
                          }}
                          formatter={(value) => [
                            value != null ? `${value}%` : "—",
                            "Seq. drop-off",
                          ]}
                          labelFormatter={(_, payload) => {
                            const p = payload && payload[0] && payload[0].payload
                            if (!p) return ""
                            return `${p.fullLesson} — ${p.program}`
                          }}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="dropoff"
                          name="Sequential drop-off %"
                          stroke="#f97316"
                          strokeWidth={2}
                          dot={{ r: 3, fill: "#f97316" }}
                          connectNulls
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </StyledLessonEngagementChart>
                </>
              )}
            </>
          )}
        </StyledInsightPanel>
        </>
        )}

        {isUsersInsights && (
        <>
        <StyledInsightPanel
          className={themeMode === "dark" ? "dark" : ""}
          style={{ width: "100%", minHeight: "auto", marginBottom: "16px" }}
        >
          <div className="panel-header">
            <div>
              <h3 className="panel-title">User Breakdown by Tier</h3>
              <div className="secondary">
                Total users are current active accounts. Active users had first-party platform activity in the selected date range.
              </div>
            </div>
            <div className="panel-actions">
              <Button
                size="small"
                variant="contained"
                onClick={openActiveNowTable}
              >
                {`Active now (${activeNowUsers.length})`}
              </Button>
            </div>
          </div>
          {userBreakdownByTier.length === 0 ? (
            <div className="empty">No user tier data available.</div>
          ) : (
            <div className="tier-breakdown-grid">
              <div className="tier-breakdown-card total">
                <div className="tier-label">All Tiers</div>
                <div className="tier-count-row">
                  <span>All users</span>
                  <strong>{userBreakdownTotals.totalUsers || 0}</strong>
                </div>
                <div className="tier-count-row">
                  <span>Active users</span>
                  <strong>{userBreakdownTotals.activeUsers || 0}</strong>
                </div>
              </div>
              {userBreakdownByTier.map((tier) => (
                <div className="tier-breakdown-card" key={tier.tierKey || tier.tierType}>
                  <div className="tier-label">{tier.label || tier.tierType}</div>
                  <div className="tier-count-row">
                    <span>All users</span>
                    <strong>{tier.totalUsers || 0}</strong>
                  </div>
                  <div className="tier-count-row">
                    <span>Active users</span>
                    <strong>{tier.activeUsers || 0}</strong>
                  </div>
                </div>
              ))}
            </div>
          )}
        </StyledInsightPanel>
        </>
        )}

        {isAiInsights && (
        <StyledInsightGrid>
          <StyledInsightPanel className={themeMode === "dark" ? "dark" : ""}>
            <div className="panel-header">
              <h3 className="panel-title">Chat: Most Used Agents</h3>
              <div className="panel-actions">
                <Tooltip title="Open drill-down">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openPanelTable("Most Used Agents", topAgents, [
                        { key: "agentName", label: "Agent", width: 220 },
                        { key: "messageCount", label: "Messages", width: 120 },
                        { key: "upvotes", label: "Upvotes", width: 120 },
                        { key: "downvotes", label: "Downvotes", width: 120 },
                      ])
                    }
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            {topAgents.length === 0 ? (
              <div className="empty">No chat data in this period.</div>
            ) : (
              <StyledInsightList>
                {topAgents.slice(0, 8).map((agent) => (
                  <li key={`${agent.agentId}-${agent.messageCount}`}>
                    <div>
                      <div className="primary">{agent.agentName || agent.agentId}</div>
                      <div className="secondary">
                        {`Upvotes ${agent.upvotes || 0} | Downvotes ${agent.downvotes || 0}`}
                      </div>
                    </div>
                    <div className="primary">{agent.messageCount || 0}</div>
                  </li>
                ))}
              </StyledInsightList>
            )}
          </StyledInsightPanel>

          <StyledInsightPanel className={themeMode === "dark" ? "dark" : ""}>
            <div className="panel-header">
              <h3 className="panel-title">Chat: Top Rated Messages</h3>
              <div className="panel-actions">
                <Tooltip title="Open drill-down">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openPanelTable("Top Rated Messages", topRatedMessages, [
                        { key: "agentName", label: "Agent", width: 180 },
                        { key: "userMessage", label: "User Message", width: 320 },
                        { key: "coachResponse", label: "Coach Response", width: 360 },
                        { key: "createdAt", label: "Created At", width: 180 },
                      ])
                    }
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            {topRatedMessages.length === 0 ? (
              <div className="empty">No positively rated messages in this period.</div>
            ) : (
              <StyledMessageList>
                {topRatedMessages.slice(0, 4).map((item) => (
                  <div className="message-item" key={`rated-${item.id}`}>
                    <div className="agent">{item.agentName || item.agentId}</div>
                    <div className="question">{item.userMessage}</div>
                    <div className="response">{item.coachResponse}</div>
                  </div>
                ))}
              </StyledMessageList>
            )}
          </StyledInsightPanel>

          <StyledInsightPanel className={themeMode === "dark" ? "dark" : ""}>
            <div className="panel-header">
              <h3 className="panel-title">Chat: Downvoted Messages</h3>
              <div className="panel-actions">
                <Tooltip title="Open drill-down">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openPanelTable("Downvoted Messages", downvotedMessages, [
                        { key: "agentName", label: "Agent", width: 180 },
                        { key: "userMessage", label: "User Message", width: 320 },
                        { key: "coachResponse", label: "Coach Response", width: 360 },
                        { key: "createdAt", label: "Created At", width: 180 },
                      ])
                    }
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            {downvotedMessages.length === 0 ? (
              <div className="empty">No downvoted messages in this period.</div>
            ) : (
              <StyledMessageList>
                {downvotedMessages.slice(0, 4).map((item) => (
                  <div className="message-item" key={`down-${item.id}`}>
                    <div className="agent">{item.agentName || item.agentId}</div>
                    <div className="question">{item.userMessage}</div>
                    <div className="response">{item.coachResponse}</div>
                  </div>
                ))}
              </StyledMessageList>
            )}
          </StyledInsightPanel>
        </StyledInsightGrid>
        )}

        {isOverviewInsights && (
        <StyledInsightGrid>
          <StyledInsightPanel className={themeMode === "dark" ? "dark" : ""}>
            <div className="panel-header">
              <h3 className="panel-title">Events: Most Popular</h3>
              <div className="panel-actions">
                <Tooltip title="Open drill-down">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openPanelTable("Most Popular Events", popularEvents, [
                        { key: "title", label: "Event", width: 260 },
                        { key: "eventType", label: "Type", width: 140 },
                        { key: "registrationCount", label: "Registrations", width: 140 },
                        { key: "uniqueMembers", label: "Unique Members", width: 140 },
                      ])
                    }
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            {popularEvents.length === 0 ? (
              <div className="empty">No event registrations in this period.</div>
            ) : (
              <StyledInsightList>
                {popularEvents.slice(0, 8).map((event) => (
                  <li key={`event-${event.eventId}`}>
                    <div>
                      <div className="primary">{event.title}</div>
                      <div className="secondary">{event.eventType}</div>
                    </div>
                    <div className="primary">{event.registrationCount || 0}</div>
                  </li>
                ))}
              </StyledInsightList>
            )}
          </StyledInsightPanel>

          <StyledInsightPanel className={themeMode === "dark" ? "dark" : ""}>
            <div className="panel-header">
              <h3 className="panel-title">Programs Being Finished</h3>
              <div className="panel-actions">
                <Tooltip title="Open drill-down">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openPanelTable("Programs Being Finished", programsCompleted, [
                        { key: "title", label: "Program", width: 260 },
                        { key: "completionCount", label: "Completions", width: 140 },
                        { key: "uniqueMembers", label: "Unique Members", width: 140 },
                      ])
                    }
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            {programsCompleted.length === 0 ? (
              <div className="empty">No program completion data in this period.</div>
            ) : (
              <StyledInsightList>
                {programsCompleted.slice(0, 8).map((program) => (
                  <li key={`program-${program.programId}`}>
                    <div className="primary">{program.title}</div>
                    <div className="primary">{program.completionCount || 0}</div>
                  </li>
                ))}
              </StyledInsightList>
            )}
          </StyledInsightPanel>

          <StyledInsightPanel className={themeMode === "dark" ? "dark" : ""}>
            <div className="panel-header">
              <h3 className="panel-title">Lessons Finished + Most Rewatched</h3>
              <div className="panel-actions">
                <Tooltip title="Open completions drill-down">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openPanelTable("Lessons Finished", lessonsCompleted, [
                        { key: "title", label: "Lesson", width: 280 },
                        { key: "totalCompletions", label: "Completions", width: 130 },
                        { key: "uniqueMembers", label: "Unique Members", width: 140 },
                      ])
                    }
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            <StyledInsightList>
              {lessonsCompleted.slice(0, 4).map((lesson) => (
                <li key={`complete-${lesson.lessonId}`}>
                  <div>
                    <div className="primary">{lesson.title}</div>
                    <div className="secondary">Finished</div>
                  </div>
                  <div className="primary">{lesson.totalCompletions || 0}</div>
                </li>
              ))}
              {lessonsMostRewatched.slice(0, 4).map((lesson) => (
                <li key={`rewatch-${lesson.lessonId}`}>
                  <div>
                    <div className="primary">{lesson.title}</div>
                    <div className="secondary">Rewatch count</div>
                  </div>
                  <div className="primary">{lesson.rewatchCount || 0}</div>
                </li>
              ))}
            </StyledInsightList>
            {lessonsMostRewatched.length > 0 && (
              <div style={{ marginTop: "8px", display: "flex", justifyContent: "flex-end" }}>
                <Tooltip title="Open rewatch drill-down">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openPanelTable("Lessons Most Rewatched", lessonsMostRewatched, [
                        { key: "title", label: "Lesson", width: 280 },
                        { key: "rewatchCount", label: "Rewatch Count", width: 130 },
                        { key: "playSessions", label: "Play Sessions", width: 130 },
                        { key: "uniqueMembers", label: "Unique Members", width: 140 },
                      ])
                    }
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </StyledInsightPanel>

        </StyledInsightGrid>
        )}

        {isAiInsights && (
        <StyledInsightGrid>
          <StyledInsightPanel className={themeMode === "dark" ? "dark" : ""}>
            <div className="panel-header">
              <h3 className="panel-title">Agent-Level Trend Over Time</h3>
              <div className="panel-actions">
                <Tooltip title="Open trend drill-down">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openPanelTable(
                        "Agent Daily Trend",
                        (agentTrend?.chartData || []).map((row, idx) => {
                          const normalized = { id: `trend-${idx}`, day: row.day }
                          ;(agentTrend?.legends || []).forEach((legend) => {
                            normalized[legend.name] = row[legend.key] || 0
                          })
                          return normalized
                        }),
                        [
                          { key: "day", label: "Day", width: 120 },
                          ...((agentTrend?.legends || []).map((legend) => ({
                            key: legend.name,
                            label: legend.name,
                            width: 140,
                          }))),
                        ]
                      )
                    }
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            {agentTrend?.chartData?.length ? (
              <StyledAgentTrendChart>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={agentTrend.chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" hide />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip />
                    <Legend />
                    {(agentTrend.legends || []).map((legend, idx) => {
                      const palette = [
                        "#0ea5e9",
                        "#a855f7",
                        "#f59e0b",
                        "#10b981",
                        "#ef4444",
                        "#6366f1",
                      ]
                      return (
                        <Line
                          key={legend.key}
                          type="monotone"
                          dataKey={legend.key}
                          name={legend.name}
                          stroke={palette[idx % palette.length]}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                        />
                      )
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </StyledAgentTrendChart>
            ) : (
              <div className="empty">No agent trend data in this period.</div>
            )}
          </StyledInsightPanel>
        </StyledInsightGrid>
        )}

        {insightsScope === "lessons" && (
        <>
        <StyledDashboardHeading style={{ fontSize: "22px", marginBottom: "12px" }}>
          Lesson completion cohorts
        </StyledDashboardHeading>
        <StyledInsightGrid>
          <StyledInsightPanel className={themeMode === "dark" ? "dark" : ""}>
            <div className="panel-header">
              <h3 className="panel-title">Retention by cohort week</h3>
              <div className="panel-actions">
                <Tooltip title="Open cohort drill-down">
                  <IconButton
                    size="small"
                    onClick={() =>
                      openPanelTable("Lesson Completion Cohorts", cohortRows, [
                        { key: "cohortWeek", label: "Cohort Week", width: 150 },
                        { key: "weekOffset", label: "Week Offset", width: 120 },
                        { key: "cohortSize", label: "Cohort Size", width: 120 },
                        { key: "activeMembers", label: "Active Members", width: 140 },
                        { key: "retentionRate", label: "Retention %", width: 120 },
                      ])
                    }
                  >
                    <OpenInFullIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            {cohortHeatmap.length === 0 ? (
              <div className="empty">No cohort data in this period.</div>
            ) : (
              <StyledHeatmap>
                <div className="heatmap-row">
                  <div className="row-label">Cohort</div>
                  {cohortWeekOffsets.map((offset) => (
                    <div className="week-label" key={`week-label-${offset}`}>
                      {`W+${offset}`}
                    </div>
                  ))}
                </div>
                {cohortHeatmap.slice(0, 6).map((row) => (
                  <div className="heatmap-row" key={`cohort-${row.cohortWeek}`}>
                    <div className="row-label">{row.cohortWeek}</div>
                    {(row.values || []).map((cell) => (
                      <div
                        className="cell"
                        key={`cell-${row.cohortWeek}-${cell.weekOffset}`}
                        style={{ backgroundColor: heatmapColor(cell.retentionRate) }}
                        title={`Retention ${cell.retentionRate}% | Drop-off ${cell.dropOffRate || 0}%`}
                      >
                        {`${Math.round(cell.retentionRate || 0)}%`}
                      </div>
                    ))}
                  </div>
                ))}
              </StyledHeatmap>
            )}
          </StyledInsightPanel>
        </StyledInsightGrid>
        </>
        )}

        {isOverviewInsights && (
        <StyledDashboardHeading style={{ fontSize: "22px", marginBottom: "12px" }}>
          Detailed Analytics
        </StyledDashboardHeading>
        )}
      </StyledModernDashboard>
      {chartContent}
      {isOverviewInsights ? (
      <StyledDashboard>
        {insightsScope === "overview" ? (
          analyticData ? (
          <>
            {analyticData.memberRegisterCount !== null && (
              <StyledInfoCard>
                <h3>Total Registered Accounts</h3>
                <div className="value">{analyticData.memberRegisterCount}</div>
              </StyledInfoCard>
            )}
            {analyticData.monthPaidMemberCount !== null && (
              <StyledInfoCard>
                <h3>Monthly Subscription</h3>
                <div className="value info">
                  <div>Total: {subscribeInfo.monthlyTotal}</div>
                  <div>{`Free Trial: ${subscribeInfo.monthlyFreeTrial}`}</div>
                  <div>{`Subscription: ${subscribeInfo.monthlySubscription}`}</div>
                  <Tooltip title="Show More Detail" className="icon-btn">
                    <IconButton
                      onClick={() => {
                        //
                        openDataTableDlg({
                          columns: subscribeTableCols,
                          rows: subscribeData,
                          title: "Subscribe Info",
                          summary: <SubscriptionSummary data={subscribeInfo} />,
                        })
                      }}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </StyledInfoCard>
            )}
            {analyticData.yearPaidMemberCount !== null && (
              <StyledInfoCard>
                <h3>Annual Subscription</h3>
                <div className="value info">
                  <div>Total: {subscribeInfo.yearlyTotal}</div>
                  <div>{`Free Trial: ${subscribeInfo.yearlyFreeTrial}`}</div>
                  <div>{`Subscription: ${subscribeInfo.yearlySubscription}`}</div>
                  <Tooltip title="Show More Detail" className="icon-btn">
                    <IconButton
                      onClick={() => {
                        openDataTableDlg({
                          columns: subscribeTableCols,
                          rows: subscribeData,
                          title: "Subscribe Info",
                          summary: <SubscriptionSummary data={subscribeInfo} />,
                        })
                      }}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </StyledInfoCard>
            )}
            {analyticData.lifePaidMemberCount !== null && (
              <StyledInfoCard>
                <h3>Lifetime Subscription</h3>
                <div className="value">
                  {subscribeInfo.liftTimeSubscription}
                </div>
                <Tooltip title="Show More Detail" className="icon-btn">
                  <IconButton
                    onClick={() => {
                      openDataTableDlg({
                        columns: subscribeTableCols,
                        rows: subscribeData,
                        title: "Subscribe Info",
                        summary: <SubscriptionSummary data={subscribeInfo} />,
                      })
                    }}
                  >
                    <BarChartIcon />
                  </IconButton>
                </Tooltip>
              </StyledInfoCard>
            )}
            {paywallData !== null && (
              <StyledInfoCard>
                <h3>Paywall Information </h3>
                <div className="value text">
                  See More info in Detail
                  <Tooltip title="Show More Detail" className="icon-btn">
                    <IconButton
                      onClick={() => {
                        openDataTableDlg({
                          columns: paywallInfoTableCols,
                          rows: paywallData,
                          title: "Paywall Information",
                        })
                      }}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </StyledInfoCard>
            )}
            {analyticData.memberActiveCount !== null && (
              <StyledInfoCard>
                <h3>Active Users</h3>
                <div className="value">{analyticData.memberActiveCount}</div>
              </StyledInfoCard>
            )}
            {avgCompletedInnercise !== null && (
              <StyledInfoCard>
                <h3>Average Completed Innercise </h3>
                <div className="value">
                  {avgCompletedInnercise.averageCompletedInnerciseTimes}
                </div>
              </StyledInfoCard>
            )}

            {completedInnercise !== null && (
              <StyledInfoCard>
                <h3>Completed Innercise</h3>
                <div className="value text">
                  See More info in Detail
                  <Tooltip title="Show More Detail" className="icon-btn">
                    <IconButton
                      onClick={() => {
                        openChartDlg(
                          completedInnerciseToBarChar({
                            list: completedInnercise,
                          })
                        )
                      }}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </StyledInfoCard>
            )}
            {mostPopularCategory !== null && (
              <StyledInfoCard>
                <h3>Most Popular Category By Views</h3>
                <div className="value text">
                  {mostPopularCategory &&
                    mostPopularCategory.length > 0 &&
                    mostPopularCategory[mostPopularCategory.length - 1].name}
                  <Tooltip title="Show More Detail" className="icon-btn">
                    <IconButton
                      onClick={() => {
                        openDataTableDlg({
                          columns: subscribeMostPopularCategoryTableCols,
                          rows: mostPopularCategory,
                          title: "Most Popular Category",
                        })
                      }}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </StyledInfoCard>
            )}

            {mostPopularCategoryBySpent !== null && (
              <StyledInfoCard>
                <h3>Most Popular Category By Spent</h3>
                <div className="value text">
                  {mostPopularCategoryBySpent &&
                    mostPopularCategoryBySpent.length > 0 &&
                    mostPopularCategoryBySpent[
                      mostPopularCategoryBySpent.length - 1
                    ].name}
                  <Tooltip title="Show More Detail" className="icon-btn">
                    <IconButton
                      onClick={() => {
                        openDataTableDlg({
                          columns: subscribeMostPopularCategoryBySpentTableCols,
                          rows: mostPopularCategoryBySpent,
                          title: "Most Popular Category By Spent",
                        })
                      }}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </StyledInfoCard>
            )}

            {mostPopularInnercise !== null && (
              <StyledInfoCard>
                <h3>Most Popular Innercise By Views </h3>
                <div className="value text">
                  {mostPopularInnercise &&
                    mostPopularInnercise.length > 0 &&
                    mostPopularInnercise[mostPopularInnercise.length - 1].title}
                  <Tooltip title="Show More Detail" className="icon-btn">
                    <IconButton
                      onClick={() => {
                        openDataTableDlg({
                          columns: subscribeMostPopularInnerciseTableCols,
                          rows: mostPopularInnercise,
                          title: "Most Innercise Category",
                        })
                      }}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </StyledInfoCard>
            )}
            {mostPopularInnerciseBySpent !== null && (
              <StyledInfoCard>
                <h3>Most Popular Innercise By Spent </h3>
                <div className="value text">
                  {mostPopularInnerciseBySpent &&
                    mostPopularInnerciseBySpent.length > 0 &&
                    mostPopularInnerciseBySpent[
                      mostPopularInnerciseBySpent.length - 1
                    ].title}
                  <Tooltip title="Show More Detail" className="icon-btn">
                    <IconButton
                      onClick={() => {
                        openDataTableDlg({
                          columns: subscribeMostPopularInnerciseSpentTableCols,
                          rows: mostPopularInnerciseBySpent,
                          title: "Most Popular Innercise By Spent",
                        })
                      }}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </StyledInfoCard>
            )}

            {remindTimeStats !== null && (
              <StyledInfoCard>
                <h3>Remind Time Stats </h3>
                <div className="value text">
                  See More info in Detail
                  <Tooltip title="Show More Detail" className="icon-btn">
                    <IconButton
                      onClick={() => {
                        openChartDlg(RemindTimeToBarChart(remindTimeStats))
                      }}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </StyledInfoCard>
            )}
            {innerciseTimeStats !== null && (
              <StyledInfoCard>
                <h3>Innercise Time Stats </h3>
                <div className="value text">
                  See More info in Detail
                  <Tooltip title="Show More Detail" className="icon-btn">
                    <IconButton
                      onClick={() => {
                        openChartDlg(
                          InnerciseTimeStatsToBarChart(innerciseTimeStats)
                        )
                      }}
                    >
                      <BarChartIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </StyledInfoCard>
            )}
          </>
        ) : (
          <div>Loading</div>
        )
        ) : null}
        <LoadingScreen loading={loading} />
        {dataTableDlg}
      </StyledDashboard>
      ) : (
        <>
          <LoadingScreen loading={loading} />
          {dataTableDlg}
        </>
      )}
    </>
  )
}

export default Home
